/**
 * Helper function to find bid increments above and supplied number if they don't actually match
 * our increment system.
 * @param bid - Number we want to check
 * @returns The low and high increments around the number.
 */
export const helperCheckIncrement = (bid:number|string): [number, number]|boolean => {
  if(typeof bid === 'string') {
    bid = parseInt(bid, 10);
  }
  if(helperInRange(bid, 0, 200)) {
    if(!((bid % 10) == 0)) {
      return [Math.floor(bid / 10) * 10, Math.ceil(bid / 10) * 10];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 201, 500)) {
    if(!((bid % 25) == 0)) {
      return [Math.floor(bid / 25) * 25, Math.ceil(bid / 25) * 25];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 501, 1000)) {
    if(!((bid % 50) == 0)) {
      return [Math.floor(bid / 50) * 50, Math.ceil(bid / 50) * 50];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 1001, 2000)) {
    if(!((bid % 100) == 0)) {
      return [Math.floor(bid / 100) * 100, Math.ceil(bid / 100) * 100];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 2001, 5000)) {
    if(!((bid % 250) == 0)) {
      return [Math.floor(bid / 250) * 250, Math.ceil(bid / 250) * 250];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 5001, 10000)) {
    if(!((bid % 500) == 0)) {
      return [Math.floor(bid / 500) * 500, Math.ceil(bid / 500) * 500];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 10001, 20000)) {
    if(!((bid % 1000) == 0)) {
      return [Math.floor(bid / 1000) * 1000, Math.ceil(bid / 1000) * 1000];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 20001, 50000)) {
    if(!((bid % 2500) == 0)) {
      return [Math.floor(bid / 2500) * 2500, Math.ceil(bid / 2500) * 2500];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 50001, 100000)) {
    if(!((bid % 5000) == 0)) {
      return [Math.floor(bid / 5000) * 5000, Math.ceil(bid / 5000) * 5000];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 100001, 200000)) {
    if(!((bid % 10000) == 0)) {
      return [Math.floor(bid / 10000) * 10000, Math.ceil(bid / 10000) * 10000];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 200001, 500000)) {
    if(!((bid % 25000) == 0)) {
      return [Math.floor(bid / 25000) * 25000, Math.ceil(bid / 25000) * 25000];
    } else {
      return [bid, bid];
    }
  } else if(helperInRange(bid, 500001, 1000000)) {
    if(!((bid % 50000) == 0)) {
      return [Math.floor(bid / 50000) * 50000, Math.ceil(bid / 50000) * 50000];
    } else {
      return [bid, bid];
    }
  } else if((bid >= 1000001)) {
    if(!((bid % 100000) == 0)) {
      return [Math.floor(bid / 100000) * 100000, Math.ceil(bid / 100000) * 100000];
    } else {
      return [bid, bid];
    }
  } else {
    return false;
  }
};

/**
 * Check if value is between min and max
 * @param val - Value to check.
 * @param min - Minimum for value.
 * @param max - Maximum for value.
 * @returns Is between min and max.
 */
const helperInRange = (val:number, min:number, max:number):boolean => {
  return (val >= min && val <= max);
};

/**
 *
 */
const incrementPattern = [
  1,
  2.5,
  5,
];

/**
 *
 */
const minPattern = [
  1,
  2,
  5,
];

/**
 * Helper to get the next bid based on current price.
 * @param currentPrice - The current price.
 * @returns The next price.
 */
export const getNextPrice = (currentPrice: number): number => {
  if(currentPrice < 200) {
    return currentPrice + 10;
  }
  if(currentPrice < 500) {
    return currentPrice + 25;
  }
  let patternIndex = 0;
  let patternLoops = 1;
  let currentIncrement = incrementPattern[patternIndex] * Math.pow(10, patternLoops - 1);
  let currentMin = minPattern[patternLoops] * Math.pow(10, patternLoops);
  let lastIncrement = currentIncrement;
  while(currentMin <= currentPrice) {
    patternIndex++;
    if(patternIndex === incrementPattern.length) {
      patternIndex = 0;
      patternLoops++;
    }
    currentIncrement = incrementPattern[patternIndex] * Math.pow(10, patternLoops - 1);
    currentMin = minPattern[patternIndex] * Math.pow(10, patternLoops);
    if(currentMin <= currentPrice) {
      lastIncrement = currentIncrement;
    }
  }
  const nextIncrement = Math.max(50, lastIncrement);
  return currentPrice + nextIncrement;
};

/**
 * Build to a price starting from zero to our next increment.
 * @param num - Our target for our increment
 * @returns Our final price increment above ouf number.
 */
export const floorNumberToIncrement = (num: number): number => {
  let price = 0;
  let lastPrice = 0;
  while(price < num) {
    lastPrice = price;
    price = getNextPrice(price);
  }
  return lastPrice;
};

/**
 * Helper to count number of bids between two numbers
 * @param start - starting bid number
 * @param end - ending bid number
 * @returns number of increments between start and end
 */
export const countIncrements = (start: number, end?: number): number => {
  if(!end || end < start) {
    return 0;
  }
  let increments = 1;
  if(start === end) {
    return increments;
  }
  let current = start;
  while(current < end) {
    current = getNextPrice(current);
    increments++;
  }
  return increments;
};
